import { EyeFilled, Loading3QuartersOutlined } from '@ant-design/icons';
import { Input, Spin } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { clearIcon, eyesIcon } from '../assets';
import CustomButton from '../../customButton';
import ErrorMessage from '../../errorMessage';
import InputTitle from '../../inputTitle';
import type { IProps } from './types';
import { useCustomInput } from './useCustomInput';
import style from './style.module.scss';

/**
 * Ant Design text, textarea or password field wrapped with a title, inline error message and loading overlay. Chooses Input.TextArea for the textarea type, takes showPW, typeInput, onChange and onClickEyeIcon from useCustomInput to toggle password visibility through a CustomButton, and renders ErrorMessage or a Spin when errorMessage or loading with hasLoading are set.
 *
 * @param props - Input value, type, title and placeholder, state flags, error message and the change, blur and key handlers.
 * @returns The rendered input wrapper element.
 */
const CustomInput = (props: IProps): React.JSX.Element => {
  const { size = 'large', isRequired = false, loading = false, hasLoading = false } = props;
  const { showPW, onClickEyeIcon, onChange, typeInput } = useCustomInput({
    name: props.name,
    type: props.type,
    value: props.value,
    onChange: props.onChange,
  });

  const InputComponent = props.type === 'textarea' ? Input.TextArea : Input;

  return (
    <div
      className={classNames(
        style.customInputWrapper,
        'custom-input-wrapper',
        props.className,
        hasLoading && '--has-loading-input',
        props.title && '--has-title-input',
      )}
    >
      <InputTitle title={props.title} isRequired={isRequired} />
      <div className={classNames(style.inputPosition, showPW ? '' : style.passwordInput)}>
        <InputComponent
          size={size}
          className={classNames(
            style.customInput,
            'custom-input',
            props.inputClassName,
            props.disabled
              ? classNames(style['customInput--disabled'], 'customInput--disabled')
              : '',
            props.isError ? classNames(style['customInput--error'], 'customInput--error') : '',
          )}
          placeholder={props.placeholder}
          type={typeInput}
          onChange={onChange}
          id={props.id}
          name={props.name}
          aria-label={props['aria-label'] ?? props.title}
          onKeyDown={props.onKeyDown}
          value={props.value}
          disabled={props.disabled}
          prefix={props.prefix}
          suffix={props.suffix}
          rows={props.rows}
          autoSize={props.autoSize}
          maxLength={props.maxLength}
          style={props.style}
          onBlur={props.onBlur}
          onPressEnter={props.onPressEnter}
          allowClear={
            props.allowClear
              ? {
                  clearIcon: <img src={clearIcon} alt="Clear icon" />,
                }
              : false
          }
        />
        {props.type === 'password' && (
          <CustomButton
            id="show-hide-password-btn"
            size="middle"
            className={style.passwordButton}
            onClick={onClickEyeIcon}
            type="tertiary"
          >
            {showPW ? (
              <EyeFilled className={style.passwordIcon} />
            ) : (
              <img width={24} height={24} className={style.passwordIcon} src={eyesIcon} alt="" />
            )}
          </CustomButton>
        )}
      </div>
      {props.errorMessage && (
        <ErrorMessage className={style.errorMessage} errorMessage={props.errorMessage} hasIcon />
      )}
      {loading && hasLoading && (
        <div className="loading-content">
          <Spin indicator={<Loading3QuartersOutlined spin />} />
        </div>
      )}
    </div>
  );
};

export default CustomInput;
